import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  Acknowledge,
  Box,
  Button,
  Container,
  Dialog,
  Fields,
  Link,
  Row,
  Stack,
  Text,
  useAlert,
  useDialog,
} from '../../components'
import { mutations, queries } from '../../graphql'
import { parseAndSetFormErrors } from '../../utils'
import { NewEmail } from '../../components/form/fields/newEmail'

export function AddEmailForm() {
  const [{ dialogProps }, { open: openDialog, close: closeDialog }] = useDialog()
  const { data: { me } = {} } = useQuery(queries.user.me, { pollInterval: 15000 })
  const { email: primaryEmail, id: userId, userEmails: userVerifiedEmails } = me ?? {}
  const { data: userEmailData, refetch: refetchAllUserEmails } = useQuery(queries.user.getUserEmails, {
    variables: { id: userId },
  })
  const [addEmailStep, setAddEmailStep] = useState('verify-email')
  const {
    register: registerNewEmail,
    handleSubmit: handleSubmitNewEmail,
    formState: formStateNewEmail,
    setError: setErrorNewEmail,
    reset: resetNewEmail,
    getValues: getValuesNewEmail,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  const {
    handleSubmit: handleSubmitPrimaryEmail,
    setError: setErrorPrimaryEmail,
    setValue: setValuesPrimaryEmail,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  const { isSubmitting: isSubmittingNewEmail, errors: errorsNewEmail = {} } = formStateNewEmail
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [addNewEmail] = useMutation(mutations.user.addNewEmail, {
    onCompleted: () => {
      setAddEmailStep('verify-email')
      openDialog()
      setValuesPrimaryEmail('newEmail', getValuesNewEmail('emailToAdd'))
      setAlert({ message: 'Add email request submitted successfully', severity: 'success' })
    },
    onError: (newErrors) => {
      parseAndSetFormErrors(newErrors, setErrorNewEmail)
      setAlert({ message: `Request failed: ${newErrors.message}`, severity: 'error' })
    },
  })

  const onSubmit = async (data) => {
    addNewEmail({
      variables: { data },
    })
  }

  const [updateEmail] = useMutation(mutations.user.updateEmail, {
    onCompleted: () => {
      setAddEmailStep('display-emails')
      setAlert({ message: 'Email successfully updated', severity: 'success' })
    },
    onError: (newErrors) => {
      parseAndSetFormErrors(newErrors, setErrorPrimaryEmail)
      setAlert({ message: `Email update failed: ${newErrors.message}`, severity: 'error' })
    },
  })

  const handlePrimaryEmailChange = async (data) => {
    updateEmail({
      variables: { data },
    })
  }

  const endAddEmailProcess = () => {
    resetNewEmail()
    closeDialog()
    setAddEmailStep('verify-email')
  }

  const dialogContent = {
    'verify-email': (
      <Stack spacing={2}>
        <Acknowledge.Success title="Success!" />
        <Stack spacing={2}>
          <Text.Body>You have successfully submitted a verification request for a new email address.</Text.Body>
          <Text.Body>
            To complete the email verification, you will need to click on the link in the email that has been sent to{' '}
            {getValuesNewEmail('emailToAdd') || '[Email placeholder]'}.
          </Text.Body>
          <Text.Body>
            If you do not receive the verification link, please email us at{' '}
            <Link href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>{process.env.REACT_APP_SUPPORT_EMAIL}</Link>
          </Text.Body>
        </Stack>
        <Button
          onClick={closeDialog}
          sx={{ width: 'fit-content', position: 'absolute', right: '32px', bottom: '32px' }}
        >
          Continue
        </Button>
      </Stack>
    ),
    'make-email-primary': (
      <form onSubmit={handleSubmitPrimaryEmail(handlePrimaryEmailChange)}>
        <Stack spacing={2}>
          <Acknowledge.Success title="Success!" />
          <Stack spacing={2}>
            <Text.Body>You have succesfully verified your new email.</Text.Body>
            <Stack spacing={0}>
              <Text.Bold>New verified email:</Text.Bold>
              <Text.Body>{getValuesNewEmail('emailToAdd') || '[Email placeholder]'}</Text.Body>
            </Stack>
            <Text.Body>
              Continue if you wish to make this new email your primary login email. Your primary email will be updated
              immediately after you confirm.
            </Text.Body>
            <Button
              type="submit"
              onClick={() => refetchAllUserEmails()}
              sx={{ width: 'fit-content', position: 'absolute', right: '32px', bottom: '32px' }}
            >
              Make Primary
            </Button>
          </Stack>
        </Stack>
      </form>
    ),
    'display-emails': (
      <Stack spacing={2}>
        <Acknowledge.Success title="Success!" />
        <Stack spacing={2}>
          <Text.Body>You have succesfully changed your primary email.</Text.Body>
          <Stack spacing={0}>
            <Text.Bold>Primary email:</Text.Bold>
            <Text.Body>{primaryEmail || '[Email placeholder]'}</Text.Body>
          </Stack>
          <Stack spacing={0}>
            <Text.Bold>Secondary emails:</Text.Bold>
            {userEmailData?.getUserEmails
              .filter((item) => item.email !== primaryEmail && item.active)
              .map((item) => (
                <Text.Body>
                  {item.email} {item.confirmedAt ? '(Verified)' : '(Unverified)'}
                </Text.Body>
              ))}
          </Stack>
          <Button
            onClick={endAddEmailProcess}
            sx={{ width: 'fit-content', position: 'absolute', right: '32px', bottom: '32px' }}
          >
            Done
          </Button>
        </Stack>
      </Stack>
    ),
  }

  return (
    <>
      <form onSubmit={handleSubmitNewEmail(onSubmit)}>
        <Stack>
          <Text.H6 data-testid="dialogSubtitle">Current Login Email Address</Text.H6>
          <Stack mx={2}>
            <Fields.Text
              data-testid="current-email-holder"
              inputProps={{
                style: {
                  color: '#333333',
                  WebkitTextFillColor: '#333333',
                },
              }}
              label="Current Primary Email"
              value={primaryEmail}
              disabled
            />
          </Stack>
          <Text.H6 data-testid="dialogTitle">Add & Confirm New Email</Text.H6>
          <Stack mx={2}>
            <Text.Body data-testid="dialogContent">
              To change your Giving Fund login email, add and confirm a new address.
            </Text.Body>
            <NewEmail register={registerNewEmail} errors={errorsNewEmail} getValues={getValuesNewEmail} />
          </Stack>
          <Row sx={{ display: 'flex', width: '100%' }}>
            <Box>
              <Text.Bold sx={{ display: 'inline' }}>Note: </Text.Bold>
              <Text.Body sx={{ display: 'inline' }}>
                Once you have confirmed the new email address, return to this screen to update.
              </Text.Body>
            </Box>
          </Row>
          <Row justifyContent="flex-end" alignItems="center" m={4}>
            <Button color="info" onClick={() => resetNewEmail()}>
              Reset
            </Button>
            <Button data-testid="add-email-submit" disabled={isSubmittingNewEmail} type="submit">
              Submit
            </Button>
          </Row>
          <Alert {...alertProps} />
        </Stack>
      </form>
      <Container maxWidth="lg">
        <Dialog
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              height: '35%',
              minHeight: 400,
            },
          }}
          {...dialogProps}
        >
          {dialogContent[addEmailStep]}
        </Dialog>
      </Container>
    </>
  )
}
